import React from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system';
  text?: string;
  raw?: any;
  query?: string | null;
};

export function MessageList({ messages, onClickTag, loading, checkTagHasPdf, onSummarizePdf, onSuggestionClick }: {
  messages: ChatMessage[];
  onClickTag: (sid: string) => void;
  loading: boolean;
  checkTagHasPdf?: (tagId: string) => boolean;
  onSummarizePdf?: (tagId: string, tagTitle?: string) => Promise<void>;
  onSuggestionClick?: (suggestion: any) => void;
}) {
  return (
    <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: '15px' }}>
      {messages.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 16 }}>Ask anything about this tour. Example: "Show me 5 tags"</Typography>
      )}

      {messages.map((m) => {
        const isUser = m.role === 'user';
        const isAssistant = m.role === 'assistant';
        return (
          <Box key={m.id} sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
            {isAssistant && (
              <Typography variant="caption" color="text.secondary">TAI</Typography>
            )}
            {m.text && !(isAssistant && m.raw?.agentId === 'pdf-agent') && (
              isUser ? (
                <Box
                  sx={{
                    display: 'inline-block',
                    bgcolor: '#EAECF0',
                    color: 'text.primary',
                    borderRadius: '12px',
                    p: '15px',
                    maxWidth: '100%',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: 16 }}>{m.text}</Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontSize: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {m.text}
                </Typography>
              )
            )}
            {isAssistant && m.raw?.agentId === 'pdf-agent' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {m.raw?.response && (
                  <Paper
                    sx={{
                      p: 2,
                      border: '1px solid #EAECF0',
                      borderRadius: '12px',
                      backgroundColor: '#FAFAFA',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontSize: 16, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {m.raw.response}
                    </Typography>
                  </Paper>
                )}

                {Array.isArray(m.raw?.results) && m.raw.results.length > 0 && m.raw.results.map((r: any, idx: number) => {
                  const url = r?.url;
                  const summary = r?.summary;
                  const tag = r?.tag || {};
                  const sid = tag?.tagSid || tag?.sid;
                  return (
                    <Paper
                      key={sid || idx}
                      sx={{
                        p: 2,
                        border: '1px solid #EAECF0',
                        borderRadius: '12px',
                        backgroundColor: '#FAFAFA',
                      }}
                    >
                      {summary && (
                        <Typography variant="body2" sx={{ fontSize: 16, mb: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {summary}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {url && (
                          <Button
                            size="small"
                            variant="outlined"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              border: '1px solid #EAECF0',
                              borderRadius: '12px',
                              color: 'text.primary',
                              textTransform: 'none',
                              px: 1.25,
                              py: 0.5,
                              '&:hover': { border: '1px solid #EAECF0', backgroundColor: 'rgba(0,0,0,0.02)' },
                            }}
                          >
                            Open PDF
                          </Button>
                        )}
                        {sid && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onClickTag(String(sid))}
                            sx={{
                              border: '1px solid #EAECF0',
                              borderRadius: '12px',
                              color: 'text.primary',
                              textTransform: 'none',
                              px: 1.25,
                              py: 0.5,
                              '&:hover': { border: '1px solid #EAECF0', backgroundColor: 'rgba(0,0,0,0.02)' },
                            }}
                          >
                            {tag?.title || 'Go to tag'}
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}
            {isAssistant && m.raw?.agentId !== 'pdf-agent' && Array.isArray(m.raw?.results) && m.raw.results.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {m.raw.results
                  .filter((t: any) => t && (t.sid || t.defaultSid) && t.title)
                  .map((t: any) => {
                    const sid = t.sid || t.defaultSid;
                    const hasPdf = checkTagHasPdf?.(String(sid)) || false;
                    return (
                      <Box key={sid} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => onClickTag(String(sid))}
                          sx={{
                            border: '1px solid #EAECF0',
                            borderRadius: '12px',
                            color: 'text.primary',
                            textTransform: 'none',
                            px: 1.25,
                            py: 0.5,
                            '&:hover': { border: '1px solid #EAECF0', backgroundColor: 'rgba(0,0,0,0.02)' },
                          }}
                        >
                          {t.title}
                        </Button>
                        {hasPdf && onSummarizePdf && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onSummarizePdf?.(String(sid), t.title)}
                            sx={{
                              border: '1px solid #FF6B35',
                              borderRadius: '12px',
                              color: '#FF6B35',
                              textTransform: 'none',
                              px: 1.25,
                              py: 0.5,
                              '&:hover': {
                                border: '1px solid #FF6B35',
                                backgroundColor: 'rgba(255,107,53,0.08)'
                              },
                            }}
                          >
                            Summarize PDF
                          </Button>
                        )}
                      </Box>
                    );
                  })}
              </Box>
            )}

            {isAssistant && m.raw?.isPdfSummary && m.raw?.pdfUrl && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  variant="outlined"
                  href={m.raw.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    border: '1px solid #FF6B35',
                    borderRadius: '12px',
                    color: '#FF6B35',
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.75,
                    fontSize: 13,
                    '&:hover': {
                      border: '1px solid #FF6B35',
                      backgroundColor: 'rgba(255,107,53,0.08)'
                    },
                  }}
                  startIcon={<span style={{ fontSize: 14 }}>📄</span>}
                >
                  Open PDF file
                </Button>
              </Box>
            )}

            {isAssistant && m.raw?.isWelcome && Array.isArray(m.raw?.suggestions) && onSuggestionClick && (
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {m.raw.suggestions.map((suggestion: any, idx: number) => (
                  <Button
                    key={idx}
                    size="small"
                    variant="outlined"
                    disabled={loading}
                    onClick={() => onSuggestionClick(suggestion)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: '12px',
                      color: 'text.primary',
                      textTransform: 'none',
                      px: 1.5,
                      py: 0.75,
                      fontSize: 13,
                      '&:hover': {
                        borderColor: 'divider',
                        backgroundColor: 'rgba(0,0,0,0.04)'
                      },
                      '&:disabled': {
                        opacity: 0.5
                      }
                    }}
                  >
                    {suggestion.label}
                  </Button>
                ))}
              </Box>
            )}

            {isAssistant && m.raw?.isTagNavigation && m.raw?.tagSuggestion && onSuggestionClick && (
              <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={loading}
                  onClick={() => onSuggestionClick(m.raw.tagSuggestion)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '12px',
                    color: 'text.primary',
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.75,
                    fontSize: 13,
                    '&:hover': {
                      borderColor: 'divider',
                      backgroundColor: 'rgba(0,0,0,0.04)'
                    },
                    '&:disabled': {
                      opacity: 0.5
                    }
                  }}
                  startIcon={<span style={{ fontSize: 14 }}>ℹ️</span>}
                >
                  {m.raw.tagSuggestion.label}
                </Button>

                {m.raw.hasPdf && onSummarizePdf && (
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={loading}
                    onClick={() => onSummarizePdf?.(m.raw.tagId, m.raw.tagTitle)}
                    sx={{
                      border: '1px solid #FF6B35',
                      borderRadius: '12px',
                      color: '#FF6B35',
                      textTransform: 'none',
                      px: 1.5,
                      py: 0.75,
                      fontSize: 13,
                      '&:hover': {
                        border: '1px solid #FF6B35',
                        backgroundColor: 'rgba(255,107,53,0.08)'
                      },
                      '&:disabled': {
                        opacity: 0.5
                      }
                    }}
                    startIcon={<span style={{ fontSize: 14 }}>📄</span>}
                  >
                    Summarize PDF
                  </Button>
                )}

                {m.raw.hasPdf && m.raw.pdfUrl && (
                  <Button
                    size="small"
                    variant="outlined"
                    href={m.raw.pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      border: '1px solid #4CAF50',
                      borderRadius: '12px',
                      color: '#4CAF50',
                      textTransform: 'none',
                      px: 1.5,
                      py: 0.75,
                      fontSize: 13,
                      '&:hover': {
                        border: '1px solid #4CAF50',
                        backgroundColor: 'rgba(76,175,80,0.08)'
                      }
                    }}
                    startIcon={<span style={{ fontSize: 14 }}>🔗</span>}
                  >
                    Open PDF
                  </Button>
                )}
              </Box>
            )}
          </Box>
        );
      })}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
          <span className="ai-dot-pulse" />
          <Typography variant="body2" sx={{ fontSize: 16 }}>Thinking…</Typography>
        </Box>
      )}
    </Box>
  );
}

